import React, { Fragment, useState, useEffect } from "react";
import { Button, Row, Col, Form, Input, Select, message } from 'antd';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDepartmentList, useAddEmployee, useEditEmployee, useCardList, useShiftDropdown } from "hooks/configuration/employee/employee";
import Image from 'helpers/Image';
import "../style.css";
const { Option } = Select;

// Same rules the API enforces in EmployeeFacade -- keep the two in step.
// Contact: Singapore numbers are 8 digits; up to 10 allowed for existing records.
const CONTACT_PATTERN = /^\d{8,10}$/;
// FIN: stored as the last 4 of the IC/FIN (e.g. 009D, per the bulk upload template),
// or a full NRIC/FIN (e.g. G1234567N).
const FIN_PATTERN = /^(\d{3}[A-Z]|[STFGM]\d{7}[A-Z])$/;

const digitsOnly = (e) => e?.target?.value?.replace(/\D/g, "");
const finInput = (e) => e?.target?.value?.toUpperCase().replace(/[^A-Z0-9]/g, "");

const FormEmployee = () => {
    let navigate = useNavigate();
    const [form] = Form.useForm();

    const editData = useLocation();
    console.log(editData)

    const create = editData?.pathname === "/configuration/employee/create";
    const contactSpan = create ? 12 : 6;
    const [serverErrors, setServerErrors] = useState({});
    const { data: cardListing } = useCardList();
    const { data: departmentListing } = useDepartmentList();
    const { data: shiftListing } = useShiftDropdown();
    const { mutate: addEmployee, isLoading: Loading } = useAddEmployee();
    const { mutate: editEmployee, isLoading: editLoading } = useEditEmployee();

    const status = [
        { 'label': "Active", "value": true },
        { 'label': "Inactive", "value": false },
    ]

    const onFinish = (formValues) => {
        console.log(formValues)
        const submitData = {
            tagId: formValues.tagId,
            employeeName: formValues.employeeName.trim(),
            finNo: formValues.finNo.trim(),
            contactNumber: formValues.contactNumber?.length ? formValues.contactNumber : null,
            designation: formValues.designation.trim(),
            companyId: formValues.companyId,
            shiftId: formValues.shiftId ?? null,
        };

        if (!create) {
            submitData.employeeId = formValues.employeeId;
            submitData.active = formValues.active;

        }

        const mutationFn = create ? addEmployee : editEmployee;
        mutationFn(submitData, {
            onSuccess: (item) => {
                if (item?.statusCode === 200) {
                    message.success(item?.message);
                    navigate("/configuration/employee");
                } else {
                    message.error(item?.message);
                }
            },
            onError: (error) => {
                if (error?.response?.data?.errorMessages) {
                    const errorMessages = error.response.data.errorMessages;
                    setServerErrors(errorMessages);
                }
            },
        });
    };
    const clearError = (name) => {
        setServerErrors(prev => ({ ...prev, [name]: "" }));
    };


    useEffect(() => {
        if (editData) {
            form.setFieldsValue({
                "employeeId": editData?.state?.employeeId,
                "tagId": editData?.state?.tagId,
                "employeeName": editData?.state?.employeeName,
                "finNo": editData?.state?.finNo,
                "designation": editData?.state?.designation,
                "contactNumber": editData?.state?.contactNumber,
                "companyId": editData?.state?.companyId,
                "shiftId": editData?.state?.shiftId,
                "active": editData?.state?.active
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editData])


    return (
        <Fragment>
            <Form
                className="form-page"
                form={form}
                colon={false}
                onFinish={onFinish}
                layout="vertical">
                <div className="form-head form-head-sticky">
                    <div className="form-head-left">
                        <Link to="/configuration/employee" className="form-back">
                            <Image name="backArrow.svg" />
                        </Link>
                        <div className="form-title">
                            <h1>{create ? "New Employee" : `Edit ${editData.state?.employeeName}`}</h1>
                            <p>{create ? "Add an employee and assign their access card" : "Update this employee's details and access card"}</p>
                        </div>
                    </div>
                    <div className="form-actions">
                        <Button className="form-cancel" onClick={() => navigate("/configuration/employee")}>Cancel</Button>
                        <Button className="form-submit" htmlType="submit" loading={Loading || editLoading}>
                            <Image className="save-img" name="save.svg" /> {create ? "Create" : "Update"}
                        </Button>
                    </div>
                </div>

                <div className="form-card">
                    <Form.Item name="employeeId" hidden={true} >
                        <Input />
                    </Form.Item>
                    <Form.Item name="tagId" hidden={true} >
                        <Input />
                    </Form.Item>

                    <section className="form-section">
                        <div className="form-section-head">
                            <h2>Employee details</h2>
                            <p>Identity, role and the access card assigned to this employee.</p>
                        </div>
                        <Row gutter={[24, 0]}>
                            <Col xs={24} sm={12} lg={6}>
                                <Form.Item label="Card ID" name="tagId" rules={[{ required: true, message: "Select the card ID!" }]}>
                                    <Select placeholder="Select card" allowClear>
                                        {!create && editData?.state?.tagId && (
                                            <Option key={editData.state.tagId} value={editData.state.tagId}>
                                                {editData.state.cardId}
                                            </Option>
                                        )}
                                        {cardListing?.data?.map((e) => (
                                            <Option key={e.tagId} value={e.tagId}>
                                                {e.cardId}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} lg={6}>
                                <Form.Item label="Employee Name" name="employeeName" rules={[{ required: true, message: "Enter the employee name!" }]}
                                    validateStatus={serverErrors?.employeeName ? "error" : ""}
                                    help={serverErrors?.employeeName?.[0]}
                                >
                                    <Input placeholder="Enter the employee name" maxLength={40} onChange={() => clearError("employeeName")}></Input>
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} lg={6}>
                                <Form.Item label="Fin No" name="finNo" getValueFromEvent={finInput}
                                    rules={[
                                        { required: true, message: "Fin No is required!" },
                                        { pattern: FIN_PATTERN, message: "Enter the last 4 of the IC/FIN (e.g. 009D) or a full FIN (e.g. G1234567N)" },
                                    ]}
                                    validateStatus={serverErrors?.finNo?.[0] ? "error" : undefined}
                                    help={serverErrors?.finNo?.[0]}>
                                    <Input placeholder="Enter the employee Fin No " maxLength={9} onChange={() => clearError("finNo")}></Input>
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} lg={6}>
                                <Form.Item label="Designation" name="designation" rules={[{ required: true, message: "Designation is required!" }]}
                                    validateStatus={serverErrors?.designation?.[0] ? "error" : undefined}
                                    help={serverErrors?.designation?.[0]}>
                                    <Input placeholder="Enter the employee Designation" maxLength={40} onChange={() => clearError("designation")}></Input>
                                </Form.Item>
                            </Col>
                        </Row>
                    </section>

                    <section className="form-section">
                        <div className="form-section-head">
                            <h2>Contact &amp; company</h2>
                            <p>How to reach this employee and which company they belong to.</p>
                        </div>
                        <Row gutter={[24, 0]}>
                            <Col xs={24} sm={12} lg={contactSpan}>
                                <Form.Item label="Contact Number" name="contactNumber" getValueFromEvent={digitsOnly}
                                    rules={[
                                        { required: true, message: "Contact Number is required!" },
                                        { pattern: CONTACT_PATTERN, message: "Enter a valid contact number (8 to 10 digits)" },
                                    ]}
                                    validateStatus={serverErrors?.contactNumber?.[0] ? "error" : undefined}
                                    help={serverErrors?.contactNumber?.[0]}
                                >
                                    <Input placeholder="Enter the employee contact number" inputMode="numeric" maxLength={10} onChange={() => clearError("contactNumber")}></Input>
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} lg={contactSpan}>
                                <Form.Item label="Contractor" name="companyId" rules={[{ required: true, message: "Contractor is required!" }]}>
                                    <Select placeholder="Select contractor">
                                        {departmentListing?.data?.map((e, a) => (<Option key={a} value={e?.departmentId}>{e?.departmentName}</Option>))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} lg={contactSpan}>
                                <Form.Item
                                    label="Shift"
                                    name="shiftId"
                                    extra="Needed for late-arrival and shift-coverage reporting. Leave blank if the employee has no fixed shift."
                                >
                                    <Select placeholder="Select shift (optional)" allowClear>
                                        {shiftListing?.data?.map((e, a) => (
                                            <Option key={a} value={e?.shiftId}>{e?.shiftTime}</Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            {create ? null : (
                                <Col xs={24} sm={12} lg={contactSpan}>
                                    <Form.Item label="Status" name="active" rules={[{ required: true, message: "Status is required!" }]}>
                                        <Select placeholder={"Select Status"} options={status} />
                                    </Form.Item>
                                </Col>
                            )}
                        </Row>
                    </section>
                </div>

            </Form>

        </Fragment>
    )
}
export default FormEmployee;